/**
 * Then vs Now — what entered, what left, what moved since the last published
 * version. Read-only: changing the plan is a governed act and stays in the
 * full platform; this app makes the change legible.
 */

import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowRight, ArrowUp, Minus, Plus, RefreshCw } from 'lucide-react';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import {
  AppCard,
  EmptyState,
  Eyebrow,
  SecondaryButton,
  StatusChip,
} from '../components/primitives';
import { splitChanges, useReviewData } from './useReviewData';
import { NO_CHANGE_MESSAGE } from './reviewLogic';

export default function ThenVsNowScreen() {
  const app = getAppDefinition('review')!;
  const navigate = useNavigate();
  const { loading, error, cycles, cycleId, setCycleId, current, history, changes, noChange, reload } =
    useReviewData();

  const { entered, exited, moved } = splitChanges(changes);
  const ranked = current?.planOfRecord?.ranked_items ?? [];

  return (
    <AppShell
      app={app}
      actions={
        <SecondaryButton onClick={reload} disabled={loading} className="!px-3 !py-1.5">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Reading…' : 'Refresh'}
        </SecondaryButton>
      }
    >
      <ScreenHeading
        title="Is the ranking still true?"
        subtitle="What entered, what left, and what moved since the last published version."
      />
      <BallLine ballWith="you — decide whether this still holds" />

      <div className="space-y-4">
        {cycles.length > 1 ? (
          <AppCard>
            <Eyebrow>Which cycle</Eyebrow>
            <select
              value={cycleId ?? ''}
              onChange={(e) => setCycleId(e.target.value)}
              className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
            >
              {cycles.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </AppCard>
        ) : null}

        <AppCard>
          <Eyebrow>Since last time</Eyebrow>
          {error ? (
            <p className="mt-2 font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
              {error}
            </p>
          ) : loading ? (
            <p className="mt-2 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
              Reading the published record…
            </p>
          ) : !current ? (
            <p className="mt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
              Nothing has been published for this cycle yet — there's no "then" to compare against.
            </p>
          ) : (
            <>
              <div className="mt-2 flex flex-wrap gap-2">
                <StatusChip label={`Version ${current.version ?? 1}`} tone="slate" />
                <StatusChip label={current.legitimacyState} tone="green" />
                {history.length > 1 ? (
                  <StatusChip label={`${history.length} versions`} tone="neutral" />
                ) : null}
              </div>
              {/* Counts only mean something when there is a "then" to count against. */}
              {noChange === 'first_version' || noChange === 'not_recorded' ? null : (
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {[
                    { label: 'Entered', n: entered.length, tone: 'green' as const },
                    { label: 'Left', n: exited.length, tone: 'coral' as const },
                    { label: 'Moved', n: moved.length, tone: 'mustard' as const },
                  ].map((m) => (
                    <div
                      key={m.label}
                      className="rounded-lg border border-gray-200 px-3 py-2 text-center dark:border-[var(--rule)]"
                    >
                      <p className="font-heading text-xl font-semibold text-gray-900 dark:text-[var(--text-pri)]">
                        {m.n}
                      </p>
                      <p className="font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                        {m.label}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {noChange ? (
                <p className="mt-3 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
                  {NO_CHANGE_MESSAGE[noChange]}
                </p>
              ) : null}
            </>
          )}
        </AppCard>

        {changes.length > 0 ? (
          <AppCard className="!p-0">
            <div className="px-5 pt-5">
              <Eyebrow>What changed</Eyebrow>
            </div>
            <ul className="mt-2">
              {changes.map((c, i) => {
                const icon =
                  c.type === 'added' ? (
                    <Plus className="h-3.5 w-3.5 text-alpine-green-600" />
                  ) : c.type === 'removed' ? (
                    <Minus className="h-3.5 w-3.5 text-luminous-coral-600" />
                  ) : (c.new_rank ?? 0) < (c.previous_rank ?? 0) ? (
                    <ArrowUp className="h-3.5 w-3.5 text-electric-mustard-600" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 text-electric-mustard-600" />
                  );
                return (
                  <li
                    key={`${c.item_id}-${i}`}
                    className="flex items-center gap-3 border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
                  >
                    {icon}
                    <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">
                      {c.item_title || c.item_id}
                    </span>
                    <span className="shrink-0 font-mono text-xs text-gray-500 dark:text-[var(--text-ter)]">
                      {c.type === 'rank_change'
                        ? `#${c.previous_rank ?? '–'} → #${c.new_rank ?? '–'}`
                        : c.type === 'added'
                          ? `new at #${c.new_rank ?? '–'}`
                          : `was #${c.previous_rank ?? '–'}`}
                    </span>
                  </li>
                );
              })}
            </ul>
          </AppCard>
        ) : null}

        {ranked.length > 0 ? (
          <AppCard className="!p-0">
            <div className="px-5 pt-5">
              <Eyebrow>The list as published · {ranked.length}</Eyebrow>
            </div>
            <ol className="mt-2">
              {ranked.slice(0, 12).map((r) => (
                <li
                  key={r.item_id}
                  className="flex items-center gap-3 border-b border-gray-100 px-5 py-2.5 last:border-0 dark:border-[var(--rule)]"
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-mono text-xs ${
                      r.above_cut_line
                        ? 'bg-electric-mustard-100 text-electric-mustard-800 dark:bg-electric-mustard-900/40 dark:text-electric-mustard-300'
                        : 'bg-gray-100 text-gray-500 dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]'
                    }`}
                  >
                    {r.rank}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-800 dark:text-[var(--text-pri)]">
                    {r.title}
                  </span>
                  {!r.above_cut_line ? <StatusChip label="Below line" tone="neutral" /> : null}
                </li>
              ))}
            </ol>
          </AppCard>
        ) : null}

        {!loading && !error && !current && cycles.length === 0 ? (
          <EmptyState
            title="No cycles to review yet"
            hint="Once a cycle publishes its priorities, its history shows up here."
          />
        ) : null}

        <div className="flex justify-end">
          <SecondaryButton onClick={() => navigate('/apps/review/challenge')}>
            What's worth challenging <ArrowRight className="h-4 w-4" />
          </SecondaryButton>
        </div>
      </div>
    </AppShell>
  );
}
